import { useContext, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import firebase from 'firebase/compat/app';
import moment from 'moment';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import { db } from '../../../firebase';
import { firebaseAuth } from '../../../providers/AuthProvider';
import { CurrentEventDataContext } from '../CurrentEventDataContext';
import { isAssignedToEvent } from '../../../utils/eventAssignment';

const PLACEHOLDER_IMG =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="600" height="280" viewBox="0 0 600 280"><rect width="600" height="280" fill="%23e4e4e7"/><text x="50%" y="50%" font-family="Inter, sans-serif" font-size="22" fill="%23a1a1aa" text-anchor="middle" alignment-baseline="middle">No banner</text></svg>',
  );

const linkCls =
  'text-xs font-medium text-zinc-700 underline-offset-2 hover:text-zinc-900 hover:underline dark:text-zinc-300 dark:hover:text-zinc-100';

export const EventCard = ({ item, updateData }) => {
  const { user } = useContext(firebaseAuth);
  const assigned = isAssignedToEvent(item, user?.email);
  const canEdit = Number(user?.role) === 1 || (assigned &&
    (Number(user?.role) <= 2 || user?.crmManager === true));
  const { setCurrentEventData } = useContext(CurrentEventDataContext);
  const navigate = useNavigate();
  const [, setEventData] = useState(null);

  const removeCloudinary = (pubId) => {
    const remove = firebase.functions().httpsCallable('appV2/removeCloudinary');
    remove(pubId)
      .then((result) => {
        if (result?.data?.message) toast.warning(result.data.message);
      })
      .catch((error) => {
        console.log(error);
        toast.error(error.message);
      });
  };

  const deleteItem = async (evt) => {
    const res = await db
      .collection('registrations')
      .where('eventId', '==', evt.id)
      .get();
    if (res.docs.length > 0) {
      alert('Delete all registrations before deleting the event.');
      return;
    }
    if (user.role !== 1) {
      alert('Only an admin can delete events.');
      return;
    }
    removeCloudinary(`pcna/ezentrum/${evt.id}/${evt.id}_banner`);
    removeCloudinary(`pcna/ezentrum/${evt.id}/${evt.id}_mobile_banner`);
    removeCloudinary(`pcna/ezentrum/${evt.id}/${evt.id}_featured_banner`);
    await db.collection('events').doc(evt.id).delete();
    toast.success('Event deleted successfully.');
    sessionStorage.removeItem('eventData');
    setEventData({});
    setCurrentEventData({});
    updateData?.();
  };

  const copyEvent = async (evt) => {
    const tmp = { ...evt };
    tmp.name = `Copy of ${tmp.name}`;
    tmp.sysname = `Copy of ${tmp.sysname}`;
    tmp.publicUrl = `${tmp.publicUrl}_copy`;
    tmp.startDate = null;
    tmp.endDate = null;
    tmp.campaignCode = '';
    tmp.allowPushToCap = false;
    tmp.campaignAltCode = '';
    tmp.campaignDescription = '';
    tmp.campaignAltDescription = '';
    tmp.campaignComments = '';
    tmp.campaignCodeVerified = false;
    tmp.capRequestId = null;
    tmp.capRequestRevision = null;
    tmp.capConfig = null;
    tmp.capSetup = null;
    tmp.capEndExportCompleted = false;
    tmp.regCount = 0;
    tmp.id = db.collection('events').doc().id;
    setCurrentEventData(tmp);
    setEventData(tmp);
    await db.collection('events').doc(tmp.id).set(tmp);
    navigate(`/manage/event/${tmp.id}`);
    toast.success('Event copied successfully.');
  };

  const archiveEvent = (evt) => {
    db.collection('events').doc(evt.id).set({ ...evt, status: 'Archived' });
    toast.success('Event archived successfully.');
    updateData?.();
  };

  const restoreEvent = (evt) => {
    db.collection('events').doc(evt.id).set({ ...evt, status: 'Active' });
    toast.success('Event restored successfully.');
    updateData?.();
  };

  const selectEvent = (evt) => {
    setCurrentEventData(evt);
    setEventData(evt);
    sessionStorage.setItem('eventData', JSON.stringify(evt));
  };

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg bg-zinc-50 shadow-sm ring-1 ring-zinc-200 dark:bg-zinc-900 dark:ring-zinc-700">
      <div className="relative aspect-[16/9] w-full bg-zinc-100 dark:bg-zinc-800">
        <img
          src={item.bannerImage || PLACEHOLDER_IMG}
          alt={item.name}
          className={clsx(
            'h-full w-full object-cover',
            !item.bannerImage && 'opacity-70',
          )}
        />
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-base font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
            {item.sysname || item.name}
          </h3>
          {item.status === 'Archived' && (
            <span className="shrink-0 rounded bg-zinc-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200">
              Archived
            </span>
          )}
        </div>

        <div className="mt-1 text-xs text-zinc-600 dark:text-zinc-300">
          {item.startDate
            ? `${moment(item.startDate).format('MM/DD/YYYY')}${
                item.endDate ? ` – ${moment(item.endDate).format('MM/DD/YYYY')}` : ''
              }${item.ongoingEvent ? ' (Ongoing)' : ''}`
            : 'Dates TBD'}
        </div>
        <div className="text-xs text-zinc-500 dark:text-zinc-400">
          Updated {moment(item.updatedAt).format('MM/DD/YYYY h:mm A')}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
          {item.allowPublic && item.publicUrl && (
            <NavLink
              to={`/${item.publicUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className={linkCls}
            >
              Landing
            </NavLink>
          )}
          {!item.disableRegistration &&
            !item.disableFrontEndRegistration &&
            item.publicUrl && (
              <NavLink
                to={`/register/${item.publicUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className={linkCls}
              >
                Reg page
              </NavLink>
            )}
          {(canEdit || user?.crmManager === true) && (
            <a
              href={`/manage/event/${item.id}`}
              onClick={() => selectEvent(item)}
              className={linkCls}
            >
              {canEdit ? 'Edit' : 'View'}
            </a>
          )}
          {canEdit && (
            <a
              href={`/manage/tickets/${item.id}`}
              onClick={() => selectEvent(item)}
              className={linkCls}
            >
              Tickets
            </a>
          )}
          <a
            href={`/manage/registrations/${item.id}`}
            onClick={() => selectEvent(item)}
            className={linkCls}
          >
            Registrations
          </a>
          {item.allowCapacity && item.capacityType === 'session' && user?.role <= 2 && (
            <a
              href={`/manage/sessions/${item.id}`}
              onClick={() => selectEvent(item)}
              className={linkCls}
            >
              Sessions
            </a>
          )}
          {item.allowGallery && user?.role <= 2 && (
            <a
              href={`/manage/gallery/${item.id}`}
              onClick={() => selectEvent(item)}
              className={linkCls}
            >
              Gallery
            </a>
          )}
          {user?.role <= 1 && (
            <button type="button" onClick={() => copyEvent(item)} className={linkCls}>
              Copy
            </button>
          )}
          {item.status === 'Active' && user?.role <= 1 && (
            <button
              type="button"
              onClick={() => archiveEvent(item)}
              className={linkCls}
            >
              Archive
            </button>
          )}
          {item.status === 'Archived' && user?.role <= 1 && (
            <button
              type="button"
              onClick={() => restoreEvent(item)}
              className={linkCls}
            >
              Restore
            </button>
          )}
          {user?.role <= 1 && (
            <button
              type="button"
              onClick={() =>
                window.confirm('Are you sure to delete this record?') &&
                deleteItem(item)
              }
              className="text-xs font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {user?.role <= 2 && (
        <div
          className={clsx(
            'mt-auto border-t px-4 py-2 text-[11px]',
            item.allowPushToCap
              ? item.campaignCodeVerified
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300'
                : 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300'
              : 'border-zinc-100 bg-zinc-50 text-zinc-500 dark:border-zinc-800 dark:bg-zinc-800 dark:text-zinc-400',
          )}
        >
          {item.allowPushToCap && (
            <>
              <div>
                {item.campaignCode}-{item.campaignDescription}
              </div>
              <div>
                {item.campaignAltCode}-{item.campaignAltDescription}
              </div>
            </>
          )}
          <div>ID: {item.id}</div>
        </div>
      )}
    </div>
  );
};
